const DAY_IN_MS = 24 * 60 * 60 * 1000;
// The backend stores prize times without a time zone (USE_TZ is off) in its TIME_ZONE.
const PRIZE_TIME_ZONE = 'Europe/Copenhagen';

/**
 * @typedef {{ status: 'Active' | 'Inactive' | 'Upcoming', is_participating: boolean, valid_from: string }} PrizeAttributes
 * @typedef {'joined' | 'today' | 'expired' | 'upcoming'} PrizeState
 */

/**
 * @param {PrizeAttributes} attributes
 * @returns {PrizeState}
 */
export function getPrizeState(attributes) {
  if (attributes.is_participating) return 'joined';
  if (attributes.status === 'Active') return 'today';
  if (attributes.status === 'Inactive') return 'expired';
  return 'upcoming';
}

/**
 * The site's locale (e.g. "da-DK"), or undefined to use the browser's.
 * @returns {string | undefined}
 */
function getLocale() {
  try {
    return Intl.getCanonicalLocales(window?.config?.iso_language_code)[0];
  } catch (e) {
    return undefined;
  }
}

/**
 * @param {string} validFrom
 * @returns {string} e.g. "Tue 15 Sep"
 */
export function formatPrizeDay(validFrom) {
  return new Date(validFrom).toLocaleDateString(getLocale(), {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
}

/**
 * @param {number} days Offset from today, e.g. 0 for "today" and 1 for "tomorrow".
 * @returns {string}
 */
export function formatRelativeDay(days) {
  return new Intl.RelativeTimeFormat(getLocale(), { numeric: 'auto' }).format(
    days,
    'day'
  );
}

/**
 * "Tomorrow" when the prize opens tomorrow, otherwise its day.
 * @param {string} validFrom
 * @returns {string}
 */
export function formatUpcomingDay(validFrom) {
  const start = new Date(validFrom);
  start.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return Math.round((start - today) / DAY_IN_MS) === 1
    ? formatRelativeDay(1)
    : formatPrizeDay(validFrom);
}

/**
 * The prize whose door starts open: the active one, else the next upcoming, else the last.
 * @param {Array<{ id: string, attributes: PrizeAttributes }>} prizes Sorted by valid_from.
 * @returns {string | undefined}
 */
export function getDefaultOpenPrizeId(prizes) {
  const prize =
    prizes.find(({ attributes }) => attributes.status === 'Active') ||
    prizes.find(({ attributes }) => attributes.status === 'Upcoming') ||
    prizes[prizes.length - 1];

  return prize?.id;
}

/**
 * The instant a prize time refers to, whatever the visitor's time zone.
 * @param {string} value A naive Copenhagen time, e.g. "2026-09-25T00:00:00".
 * @returns {number} Milliseconds since the epoch.
 */
export function parsePrizeTime(value) {
  // Read the wall-clock time as UTC, then shift it by Copenhagen's offset at that moment.
  const asUtc = Date.parse(`${value.slice(0, 19)}Z`);
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: PRIZE_TIME_ZONE,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric'
    })
      .formatToParts(asUtc)
      .map(({ type, value: part }) => [type, Number(part)])
  );
  const inCopenhagen = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );

  return asUtc - (inCopenhagen - asUtc);
}

/**
 * @param {number} ms
 * @returns {string} e.g. "07:42:10". Hours are not wrapped, so two days read "48:00:00".
 */
export function formatCountdown(ms) {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return [
    Math.floor(seconds / 3600),
    Math.floor(seconds / 60) % 60,
    seconds % 60
  ]
    .map(part => String(part).padStart(2, '0'))
    .join(':');
}

/**
 * What the countdown counts to: the active prize closing, else the next one opening.
 * @param {Array<{ attributes: PrizeAttributes & { valid_to: string } }>} prizes Sorted by valid_from.
 * @returns {{ kind: 'closes' | 'opens', at: number } | null}
 */
export function getCountdownTarget(prizes) {
  const active = prizes.find(
    ({ attributes }) => attributes.status === 'Active'
  );
  if (active) {
    return { kind: 'closes', at: parsePrizeTime(active.attributes.valid_to) };
  }

  const upcoming = prizes.find(
    ({ attributes }) => attributes.status === 'Upcoming'
  );
  if (upcoming) {
    return {
      kind: 'opens',
      at: parsePrizeTime(upcoming.attributes.valid_from)
    };
  }

  return null;
}
